import { useEffect, useState, useCallback } from 'react';
import { Key, Check, AlertCircle, Trash2, Save, RefreshCw, Server, User, Layers } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { invalidKeyMessage, saveRefusalMessage, unsafeUrlMessage } from './accessTokenCopy';
import AxiomScopePicker from './AxiomScopePicker';
import './Admin.css';

// Keys token-resolver.js's providerForKey() maps to a provider — the ones the
// tenant policy's allowUserKeys gates (PUT /api/secrets/me/:key: "a policy
// with allowUserKeys false refuses personal provider settings", clearing
// stays allowed). persona_token/axiom_token/axiom_scope are nyxCore
// integrations, not LLM provider credentials, so they are unaffected.
const PROVIDER_KEYS = new Set(['gemini_key', 'openai_key', 'anthropic_key', 'mistral_key', 'ollama_url']);

const KEY_META = {
  persona_token:  { label: 'Persona Studio Token',  prefix: 'nyx_pa_', desc: 'Verbindet dich mit deinen eigenen Persona-Circles im nyxCore-Dashboard.', icon: '🎭' },
  axiom_token:    { label: 'Axiom RAG Token',       prefix: 'nyx_ax_', desc: 'Persönliche Wissensdatenbank — Persona-Antworten greifen auf deine Dokumente zu.', icon: '📚' },
  axiom_scope:    { label: 'Axiom Scope',           prefix: 'Projekt-ID', secret: false, icon: '🎯',
                    desc: 'Nur nötig, wenn dein Axiom-Token mehr als ein Projekt oder eine Collection abdeckt — dann kann Axiom das Ziel nicht selbst erraten. Wähle aus, worin Etch suchen soll.' },
  openai_key:     { label: 'OpenAI API-Key',        prefix: 'sk-',     desc: 'Eigener OpenAI-Schlüssel. Überschreibt den Tenant-Default.', icon: '🤖' },
  anthropic_key:  { label: 'Anthropic API-Key',     prefix: 'sk-ant-', desc: 'Eigener Anthropic-Claude-Schlüssel. Überschreibt den Tenant-Default.', icon: '🦊' },
  gemini_key:     { label: 'Google Gemini API-Key', prefix: 'AIza',    desc: 'Eigener Gemini-Schlüssel. Überschreibt den Tenant-Default.', icon: '✨' },
  mistral_key:    { label: 'Mistral API-Key',       prefix: '',        desc: 'Eigener Mistral-Schlüssel (EU-gehostet). Überschreibt den Tenant-Default.', icon: '🌬️' },
  ollama_url:     { label: 'Ollama Base-URL',       prefix: 'http',    desc: 'Eigene lokale Ollama-Instanz statt der vom Tenant.', icon: '🦙' },
  // Tenant-scope only: the region rides with the *tenant's* OpenAI key
  // (resolveOpenAIRegion() keys the check to which tier the key itself came
  // from), and a member-tier override here would let a member route the
  // tenant's shared key around euOnly on their own — see the finding this
  // closes. Anything other than the exact string 'eu' reads as 'global'
  // (provider-resolver.js), so this is a select, not free text.
  openai_region:  { label: 'OpenAI-Region',         prefix: '',        secret: false, icon: '🌍',
                    desc: 'Region für den Tenant-OpenAI-Key. „EU" nötig, damit dieser Key unter „Nur EU-Verarbeitung" nutzbar ist.', scope: 'tenant' },
};

const SOURCE_META = {
  user:   { Icon: User,   label: 'Eigener Override', tone: 'is-user',   description: 'Dein persönlicher Wert.' },
  tenant: { Icon: Layers, label: 'Tenant-Default',   tone: 'is-tenant', description: 'Vom Mandanten-Admin gesetzt.' },
  env:    { Icon: Server, label: 'System-Default',   tone: 'is-env',    description: 'Aus der .env vom Server.' },
  none:   { Icon: AlertCircle, label: 'Nicht eingerichtet', tone: 'is-missing', description: 'Kein Wert gefunden.' },
};

export default function AccessTokens({ scope = 'me' }) {
  const { token } = useAuth();
  const [settings, setSettings] = useState({});
  const [tenantInfo, setTenantInfo] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState(null);
  // Which row `error` belongs to — null for a page-level failure (e.g. the
  // initial GET), a key for a save/clear refusal, so it renders at the row
  // that actually failed instead of only as a page banner nobody can place.
  const [errorKey, setErrorKey] = useState(null);
  const [savedKey, setSavedKey] = useState(null);
  const [warningKey, setWarningKey] = useState(null);
  const [policy, setPolicy] = useState(null);
  // Free text only when nyxCore cannot list the token's targets.
  const [scopeFallback, setScopeFallback] = useState(false);

  const url = scope === 'tenant' ? '/api/secrets/tenant' : '/api/secrets/me';

  // Only scope="me" needs to know whether the tenant lets members bring their
  // own provider keys — the tenant-defaults screen (scope="tenant") is where
  // that policy is *set*, via LlmPolicySettings, so it never disables itself.
  useEffect(() => {
    if (scope !== 'me') return undefined;
    let cancelled = false;
    fetch('/api/secrets/policy', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (!cancelled && data) setPolicy(data.policy || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [scope, token]);

  const userKeysDisabled = scope === 'me' && policy?.allowUserKeys === false;

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorKey(null);
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json()).error || 'Fehler beim Laden');
      const data = await res.json();
      setSettings(data.settings || {});
      setTenantInfo(data.tenant || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [url, token]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  async function saveKey(key) {
    const value = draft[key]?.trim();
    if (!value) return;
    setSavingKey(key);
    setError(null);
    setErrorKey(null);
    setWarningKey(k => (k === key ? null : k));
    try {
      const res = await fetch(`${url}/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ value }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 422 invalid_key: the provider itself refused this key — spell out
        // why instead of surfacing the bare error code (checkProviderKey() in
        // routes/secrets.js). 422 unsafe_url: the outbound-URL guard refused
        // it before it ever reached a provider (outboundUrlViolation()).
        if (res.status === 422 && body.error === 'invalid_key') {
          setError(invalidKeyMessage(body.reason));
        } else if (res.status === 422 && body.error === 'unsafe_url') {
          setError(unsafeUrlMessage(body.reason));
        } else {
          setError(saveRefusalMessage(body.error));
        }
        setErrorKey(key);
        return;
      }
      setDraft(d => ({ ...d, [key]: '' }));
      // `verified` is absent for settings that are not checkable provider
      // keys (checkProviderKey() returns {} for those) — only an explicit
      // `false` means "saved, but the provider could not confirm it".
      if (body.verified === false) setWarningKey(key);
      setSavedKey(key);
      setTimeout(() => setSavedKey(null), 2000);
      await fetchSettings();
    } catch (err) {
      setError(err.message);
      setErrorKey(key);
    } finally {
      setSavingKey(null);
    }
  }

  async function clearKey(key) {
    if (!confirm('Override wirklich entfernen? Es greift dann der Tenant-Default oder System-Default.')) return;
    setSavingKey(key);
    setError(null);
    setErrorKey(null);
    setWarningKey(k => (k === key ? null : k));
    try {
      const res = await fetch(`${url}/${key}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Entfernen fehlgeschlagen');
      await fetchSettings();
    } catch (err) {
      setError(err.message);
      setErrorKey(key);
    } finally {
      setSavingKey(null);
    }
  }

  if (loading) {
    return <div role="status" aria-live="polite" className="admin-loading">Lade…</div>;
  }

  return (
    <div className="admin-stack">
      <header className="admin-section-header">
        <div>
          <p className="eyebrow">Einstellungen · {scope === 'tenant' ? 'Tenant-Defaults' : 'Persönliche Zugänge'}</p>
          <h1 className="admin-section-title" id="access-tokens-heading" style={{ fontSize: '1.625rem' }}>
            {scope === 'tenant' ? 'Tenant-weite Token & API-Keys' : 'Persönliche Token & API-Keys'}
          </h1>
          <p className="admin-section-desc">
            {scope === 'tenant'
              ? 'Diese Werte gelten für alle Mitglieder dieses Mandanten — solange ein einzelner Nutzer keinen eigenen Override gesetzt hat.'
              : 'Deine persönlichen Zugänge überschreiben die vom Mandanten-Admin gesetzten Defaults. Lass ein Feld leer, um den Default zu nutzen.'}
            {' '}Werte werden mit AES-256-GCM verschlüsselt gespeichert.
          </p>
          {tenantInfo && (
            <div className="tenant-banner">
              <span>Mandant:</span>
              <strong>{tenantInfo.slug || tenantInfo.id || 'lokal'}</strong>
              {tenantInfo.role && <span className={`tenant-role-pill role-${tenantInfo.role.toLowerCase()}`}>{tenantInfo.role}</span>}
            </div>
          )}
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={fetchSettings} title="Neu laden">
          <RefreshCw size={14} aria-hidden="true" />
          <span>Neu laden</span>
        </button>
      </header>

      {error && !errorKey && <div role="alert" className="admin-error">{error}</div>}

      <div className="key-row-grid">
        {Object.entries(KEY_META)
          .filter(([, meta]) => !meta.scope || meta.scope === scope)
          .map(([key, meta]) => {
          const setting = settings[key] || { configured: false, source: 'none' };
          const sourceMeta = SOURCE_META[setting.source] || SOURCE_META.none;
          const isSaving = savingKey === key;
          const justSaved = savedKey === key;
          const isUnverified = warningKey === key;
          const disabledByPolicy = userKeysDisabled && PROVIDER_KEYS.has(key);
          const disabledHintId = disabledByPolicy ? `${key}-policy-hint` : undefined;
          const scopePicker = key === 'axiom_scope' && !scopeFallback;
          return (
            <div key={key} className={`key-row ${setting.configured ? 'configured' : ''}`}>
              <div className="key-row-icon" aria-hidden="true">
                <span style={{ fontSize: 22 }}>{meta.icon}</span>
              </div>
              <div className="key-row-body">
                <div className="key-row-head">
                  <h3>{meta.label}</h3>
                  <span className={`prov-status ${sourceMeta.tone}`}>
                    <sourceMeta.Icon size={11} aria-hidden="true" />
                    {sourceMeta.label}
                  </span>
                </div>
                <p className="key-row-desc">{meta.desc}</p>
                {!scopePicker && setting.configured && (setting.value || setting.masked) && (
                  <p
                    className="key-row-current"
                    title={meta.secret === false
                      ? 'Aktuell aktiver Wert'
                      : 'Aktuell aktiver Wert – nur Anfang und Ende sichtbar'}
                  >
                    <span className="key-row-current-label">Aktiver Wert:</span>
                    <code className="key-row-current-value">{setting.value || setting.masked}</code>
                  </p>
                )}
                {disabledByPolicy && (
                  <p id={disabledHintId} className="key-row-desc">
                    <span className="badge badge-warning">Zentral verwaltet</span>{' '}
                    Dein Mandant verwaltet die KI-Zugänge zentral.
                  </p>
                )}
                {isUnverified && (
                  <p className="key-row-desc" role="status">
                    <span className="badge badge-warning">Ungeprüft</span>{' '}
                    Key gespeichert, aber nicht prüfbar.
                  </p>
                )}
                {errorKey === key && (
                  <p role="alert" className="key-row-error">{error}</p>
                )}
                {scopePicker && (
                  <AxiomScopePicker
                    targetsUrl={`${url}/axiom-targets`}
                    saveUrl={`${url}/axiom_scope`}
                    authToken={token}
                    storedValue={setting.value || null}
                    tokenFingerprint={settings.axiom_token?.masked || null}
                    canClear={setting.source === (scope === 'tenant' ? 'tenant' : 'user')}
                    onClear={() => clearKey(key)}
                    onSaved={fetchSettings}
                    onFallback={() => setScopeFallback(true)}
                  />
                )}
                <div className="key-row-input" hidden={scopePicker}>
                  {key === 'openai_region' ? (
                    <select
                      className="input"
                      value={draft[key] || ''}
                      onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
                      disabled={isSaving || disabledByPolicy}
                      aria-describedby={disabledHintId}
                      aria-label={`${meta.label} wählen`}
                    >
                      <option value="">– auswählen –</option>
                      <option value="global">Global</option>
                      <option value="eu">EU</option>
                    </select>
                  ) : (
                    <input
                      type={meta.secret === false ? 'text' : 'password'}
                      className="input"
                      placeholder={`${meta.prefix}…`}
                      value={draft[key] || ''}
                      onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
                      disabled={isSaving || disabledByPolicy}
                      aria-describedby={disabledHintId}
                      aria-label={`${meta.label} eingeben`}
                    />
                  )}
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => saveKey(key)}
                    disabled={!draft[key]?.trim() || isSaving || disabledByPolicy}
                  >
                    {justSaved ? (
                      <>
                        <Check size={14} aria-hidden="true" />
                        <span>Gespeichert</span>
                      </>
                    ) : (
                      <>
                        <Save size={14} aria-hidden="true" />
                        <span>{isSaving ? 'Speichere…' : 'Speichern'}</span>
                      </>
                    )}
                  </button>
                  {setting.source === (scope === 'tenant' ? 'tenant' : 'user') && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => clearKey(key)}
                      disabled={isSaving}
                      title={scope === 'tenant' ? 'Tenant-Default entfernen' : 'Eigenen Override entfernen'}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
